/**
 * De volledige tomtom van één kar, als los venster voor een tweede monitor.
 *
 * De werkvloer opent dit met `window.open`. Er zit bewust geen navigatie omheen: dit
 * venster heeft één taak, de hele route in één oogopslag laten zien terwijl de operator
 * op de andere monitor werkt. Het ververst zichzelf elke tien seconden, want er wordt
 * niet op geklikt.
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { api, type KarAntwoord } from '../api';
import { tijd } from '../gegevens';
import { Routepaneel } from '../onderdelen/Routepaneel';

export function Tomtomscherm() {
  const { karId } = useParams<{ karId: string }>();
  const [antwoord, setAntwoord] = useState<KarAntwoord | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const [ververstOp, setVerverstOp] = useState<Date | null>(null);

  // Het bouwnummer zit in het karnummer: `0719-25-6504-10-01`.
  const bouwnummer = karId?.split('-')[0];

  const haal = useCallback(async () => {
    if (!bouwnummer || !karId) return;
    try {
      setAntwoord(await api.kar(bouwnummer, karId));
      setVerverstOp(new Date());
      setFout(null);
    } catch (e) {
      setFout(e instanceof Error ? e.message : 'De kar kon niet geladen worden.');
    }
  }, [bouwnummer, karId]);

  useEffect(() => {
    void haal();
    const tikker = window.setInterval(() => void haal(), 10_000);
    return () => window.clearInterval(tikker);
  }, [haal]);

  if (fout) return <div className="hs-melding" style={{ margin: 16 }}>{fout}</div>;
  if (!antwoord) return <div className="hs-leeg">De tomtom wordt geladen…</div>;

  const { kar } = antwoord;

  return (
    <div style={{ padding: 14, background: 'var(--app-achtergrond)', minHeight: '100vh' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 10,
          flexWrap: 'wrap',
          marginBottom: 10,
        }}
      >
        <h1 className="hs-koptitel">{kar.nummer}</h1>
        <span className="hs-mono hs-muted">{kar.kar} {kar.plaatsing.karNaam}</span>
        <span className="hs-muted">
          {kar.plaatsing.karNaam} · {kar.bouwelementen.length}{' '}
          {kar.bouwelementen.length === 1 ? 'bouwelement' : 'bouwelementen'} · {kar.onderdelen}{' '}
          onderdelen · {kar.stuks} stuks
        </span>
        <span className="hs-chip" style={{ marginLeft: 'auto' }}>
          <span className="hs-live" />
          Live · {ververstOp ? tijd(ververstOp.toISOString()) : ''}
        </span>
      </div>

      <Routepaneel route={kar.route} positie={kar.positie} onStapKiezen={() => undefined} />

      <p className="hs-muted" style={{ marginTop: 10 }}>
        Stap {Math.min(kar.positie + 1, kar.route.length)} van {kar.route.length}
        {kar.reworkActief
          ? ` · rework aangevraagd door ${kar.reworkActief.aangevraagdDoor}: ${kar.reworkActief.reden}`
          : ''}
      </p>
    </div>
  );
}
